import React from 'react'
import { DurationInput, tryParseDuration } from './DurationInput'
import type { InlineInputContentOptions, InlineInputProps } from '../InlineInput'
import { InlineInput } from '../InlineInput'
import { IconValueBlock } from '@ztechapps/webui.util'
import { Duration, DisplayAs, formatNumber } from '@ztechapps/common.utils'
import type { WorkdayConfig } from '@ztechapps/common.contract'
import isNil from 'lodash/isNil'

/**
 * Input-level props forwarded to the underlying `DurationInput`.
 * Kept intentionally narrow so call sites get IntelliSense for duration-specific options.
 */
export type DurationInlineInputInputProps = {
  workdayConfig?: WorkdayConfig
  displayAs?: DisplayAs
  disabled?: boolean
  placeholder?: string
  step?: number
}

export type DurationInlineInputProps = InlineInputProps<DurationInlineInputInputProps>

type DurationContentProps = {
  value: number
  workdayConfig?: WorkdayConfig
  displayAs?: DisplayAs
}

export const DurationDisplayContent = ({ value, workdayConfig, displayAs = DisplayAs.Hours }: DurationContentProps) => {
  const formatted = Duration.format(value, { workdayConfig, displayAs })
  const humanized = Duration.humanize(value, workdayConfig)
  const help = humanized ? `${humanized} (${formatNumber(value)} minutes total)` : ''
  return <IconValueBlock icon="clock" value={formatted} help={help} />
}

type DurationEmptyContentProps = Omit<DurationContentProps, 'value'>

const DurationEmptyContent = ({ workdayConfig, displayAs = DisplayAs.Hours }: DurationEmptyContentProps) => {
  const formatted = Duration.format(0, { workdayConfig, displayAs })
  return <IconValueBlock icon="clock" value={formatted} />
}

const formatValue = (value: number, workdayConfig?: WorkdayConfig, displayAs: DisplayAs = DisplayAs.Hours) => {
  return Duration.format(value, { workdayConfig, displayAs })
}

export const DurationEditContent = ({ setNewValue, value, inputProps, ...props }: InlineInputContentOptions<DurationInlineInputInputProps>) => {
  const workdayConfig = inputProps?.workdayConfig
  const displayAs = inputProps?.displayAs ?? DisplayAs.Hours
  const [displayString, setDisplayString] = React.useState(
    typeof value === 'number' ? formatValue(value, workdayConfig, displayAs) : (value ?? '')
  )

  const handleChange = (e: any) => {
    const raw = e?.target?.value
    setDisplayString(raw)
    const parsed = tryParseDuration(raw, workdayConfig)
    if (!isNil(parsed)) setNewValue(parsed)
  }

  return (
    <DurationInput
      {...inputProps}
      value={displayString}
      onChange={handleChange}
      onBlur={props.onBlur}
      onKeyDown={props.onKeyDown}
      disabled={props.submitting}
    />
  )
}

export const DurationInlineInput = ({
  name = 'duration',
  displayContent,
  emptyContent,
  editContent,
  parentInputComponent,
  inputProps,
  ...rest
}: DurationInlineInputProps) => {
  const ParentInput = parentInputComponent ?? InlineInput
  const workdayConfig = inputProps?.workdayConfig
  const displayAs = inputProps?.displayAs ?? DisplayAs.Hours
  displayContent =
    displayContent ??
    ((initialValue: number) => <DurationDisplayContent value={initialValue} workdayConfig={workdayConfig} displayAs={displayAs} />)
  editContent = editContent ?? DurationEditContent
  emptyContent = emptyContent ?? (() => <DurationEmptyContent workdayConfig={workdayConfig} displayAs={displayAs} />)

  return (
    <ParentInput
      {...{
        name,
        ...rest,
        inputProps,
        displayContent,
        editContent,
        emptyContent,
      }}
    />
  )
}
